import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  AlignLeft,
  Briefcase,
  Download,
  FileText,
  Flag,
  GraduationCap,
  Languages,
  Mail,
  MapPin,
  Phone,
  Quote,
  Shield,
  Target,
  Trash2,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { DockedPane, PaneBack, PaneClose } from '../pane/DockedPane.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { formatDay, formatYears } from '../ui/format.js';
import { SectionIcon } from '../ui/SectionIcon.js';
import { useToast } from '../ui/Toast.js';
import { saidNoService, shortAnswer, type ShortAnswer } from './answers.js';
import { CandidateTile } from './CandidateTile.js';
import { CvViewer } from './CvViewer.js';
import { IconFact } from './IconFact.js';
import { LinkedinIcon } from './LinkedinIcon.js';
import { useTalentTRPC } from './trpc.js';

/** An answer with more values than this reads better as chips than as a sentence. */
const MAX_LISTED_VALUES = 3;

/**
 * One candidate in the docked pane, in the order a recruiter decides: who
 * they are and how to reach them, what they want, what they have done, then
 * the CV itself.
 */
export function CandidatePane(props: { id: string; onClose: () => void }): React.JSX.Element {
  const { id, onClose } = props;
  const trpc = useTalentTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const candidate = useQuery(trpc.talent.get.queryOptions({ id }));
  const [confirming, setConfirming] = useState(false);
  const [viewing, setViewing] = useState(false);

  const remove = useMutation(
    trpc.talent.remove.mutationOptions({
      onSuccess: () => {
        setConfirming(false);
        onClose();
        void queryClient.invalidateQueries({ queryKey: trpc.talent.search.queryKey() });
      },
      onError: (error) => toast(error.message),
    }),
  );

  const person = candidate.data;
  const answer = (questionId: string) =>
    person === undefined ? null : shortAnswer(person.answers, questionId);
  const noService = person !== undefined && saidNoService(person.answers);
  const role =
    person === undefined
      ? null
      : person.currentTitle !== null && person.currentEmployer !== null
        ? `${person.currentTitle} at ${person.currentEmployer}`
        : (person.currentTitle ?? person.currentEmployer);
  const read =
    person === undefined
      ? null
      : person.parseError !== null
        ? person.parseError
        : person.parsedAt === null
          ? 'Reading CV…'
          : null;
  const military = person?.military ?? null;
  const serviceRecord =
    military === null
      ? null
      : [
          military.country,
          military.branch,
          military.rank,
          military.yearsServed === null ? null : formatYears(military.yearsServed),
        ]
          .filter((part) => part !== null)
          .join(' · ');

  return (
    <DockedPane label="Candidate" onClose={onClose}>
      <header className="pane-head">
        <PaneBack onClose={onClose} />
        <div className="candidate-head">
          <CandidateTile key={id} candidate={person} />
          <div className="candidate-head-text">
            <strong className="candidate-name" role="heading" aria-level={2}>
              {person?.name ?? '…'}
            </strong>
            {person?.headline != null && <p className="candidate-headline">{person.headline}</p>}
          </div>
          <span className="pane-head-actions">
            <PaneClose onClose={onClose} />
          </span>
        </div>
        {person !== undefined && (
          <>
            <div className="candidate-facts">
              <IconFact icon={MapPin} label="Location" text={person.location} />
              <IconFact
                icon={Briefcase}
                label="Experience"
                text={person.yearsExperience === null ? null : formatYears(person.yearsExperience)}
              />
              <IconFact icon={Flag} label="Nationality" text={person.nationality.join(', ')} />
              <IconFact icon={Languages} label="Languages" text={person.languages.join(', ')} />
            </div>
            <div className="candidate-contacts">
              <a className="button small" href={`mailto:${person.email}`} title={person.email}>
                <Mail aria-hidden="true" />
                Email
              </a>
              {person.phone !== null && (
                <a
                  className="button small"
                  href={`tel:${person.phone.replace(/[^0-9+]/g, '')}`}
                  title={person.phone}
                >
                  <Phone aria-hidden="true" />
                  Phone
                </a>
              )}
              {person.linkedinUrl !== null && (
                <a
                  className="button small"
                  href={person.linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkedinIcon />
                  LinkedIn
                </a>
              )}
            </div>
          </>
        )}
      </header>

      {candidate.isError && (
        <p className="subtle">Could not load them: {candidate.error.message}</p>
      )}
      {person !== undefined && (
        <>
          <div className="candidate-actions-block">
            <div className="talent-actions">
              <button
                className="button primary"
                type="button"
                aria-pressed={viewing}
                onClick={() => setViewing((open) => !open)}
              >
                <FileText aria-hidden="true" />
                View CV
              </button>
              <a className="button" href={`/talent-api/cv/${id}`} download>
                <Download aria-hidden="true" />
                Download
              </a>
              <button
                className="text-button danger candidate-delete"
                type="button"
                onClick={() => setConfirming(true)}
              >
                <Trash2 aria-hidden="true" />
                Delete
              </button>
            </div>
            <p className="subtle">
              Submitted {formatDay(person.submittedAt)}
              {read !== null && ` · ${read}`}
            </p>
            {viewing && <CvViewer id={id} name={person.name} onClose={() => setViewing(false)} />}
          </div>

          <RowSection
            title="Looking for"
            icon={Target}
            rows={[
              answerRow(answer('positions')),
              answerRow(answer('positions_other')),
              answerRow(answer('expertise')),
              answerRow(answer('expertise_other')),
            ]}
          />

          {!noService && (
            <RowSection
              title="Service"
              icon={Shield}
              rows={[
                answerRow(answer('branch')),
                answerRow(answer('countries_experience')),
                answerRow(answer('roles')),
                answerRow(answer('deployed_abroad')),
                answerRow(answer('deployments')),
                answerRow(answer('clearance')),
                row('On the CV', serviceRecord),
                row('Role on the CV', military?.role ?? null),
              ]}
            />
          )}

          <RowSection
            title="Background"
            icon={GraduationCap}
            rows={[
              row('Current role', role),
              answerRow(answer('country_from')),
              answerRow(answer('citizenships')),
              noService ? row('Service', 'No military service') : null,
              person.education.length === 0
                ? null
                : row(
                    'Education',
                    person.education.map((entry) => (
                      <span
                        key={`${entry.school}|${entry.programme}|${entry.graduatedIn}`}
                        className="candidate-line"
                      >
                        {[entry.school, entry.programme, entry.graduatedIn]
                          .filter((part) => part !== null)
                          .join(' · ')}
                      </span>
                    )),
                  ),
            ]}
          />

          {person.summary !== null && (
            <Section title="Summary" icon={AlignLeft}>
              <p className="candidate-summary">{person.summary}</p>
            </Section>
          )}

          {person.skills.length > 0 && (
            <Section title="Skills" icon={Wrench}>
              <Chips values={person.skills} />
            </Section>
          )}

          {person.note !== null && (
            <Section title="Note from the candidate" icon={Quote}>
              <blockquote className="candidate-note">{person.note}</blockquote>
            </Section>
          )}

          {person.cvText !== null && (
            <details className="pane-disclosure candidate-cv-text">
              <summary>CV text</summary>
              <pre className="cv-text">{person.cvText}</pre>
            </details>
          )}
        </>
      )}

      {confirming && (
        <ConfirmModal
          title="Delete this candidate and their CV?"
          confirmLabel="Delete"
          danger
          onConfirm={() => remove.mutate({ id })}
          onCancel={() => setConfirming(false)}
        />
      )}
    </DockedPane>
  );
}

function Section(props: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="candidate-section">
      <h3 className="candidate-section-title">
        <SectionIcon icon={props.icon} />
        {props.title}
      </h3>
      {props.children}
    </section>
  );
}

interface RowData {
  label: string;
  value: React.ReactNode;
}

/** An empty fact is no row at all, never a label over a blank. */
function row(label: string, value: React.ReactNode): RowData | null {
  return value === null || value === '' ? null : { label, value };
}

function answerRow(answer: ShortAnswer | null): RowData | null {
  if (answer === null) return null;
  return row(
    answer.label,
    answer.values.length > MAX_LISTED_VALUES ? (
      <Chips values={answer.values} />
    ) : (
      answer.values.join(', ')
    ),
  );
}

/** Label and value rows under a heading; a section with no rows is left out, heading and all. */
function RowSection(props: {
  title: string;
  icon: LucideIcon;
  rows: (RowData | null)[];
}): React.JSX.Element | null {
  const rows = props.rows.filter((entry) => entry !== null);
  if (rows.length === 0) return null;
  return (
    <Section title={props.title} icon={props.icon}>
      <dl className="candidate-rows">
        {rows.map((entry) => (
          <div key={entry.label} className="candidate-row">
            <dt>{entry.label}</dt>
            <dd>{entry.value}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function Chips(props: { values: readonly string[] }): React.JSX.Element {
  return (
    <span className="talent-skills">
      {props.values.map((value) => (
        <span key={value} className="skill-chip">
          {value}
        </span>
      ))}
    </span>
  );
}
